import React, { createRef } from "react";
import Peer from "peerjs";
import {
  faPhoneSlash,
  faCamera,
  faPen,
  faTrash,
  faSyncAlt,
  faMicrophoneAlt,
  faMicrophoneAltSlash,
  faVideo,
  faVideoSlash,
  faServer,
  faSearchPlus,
  faSearchMinus,
  faComment,
  faCircle,
  faStopCircle,
} from "@fortawesome/free-solid-svg-icons";

import StatusWebRTC from "./StatusWebRTC";
import CallAlertWebRTC from "./CallAlertWebRTC";
import Camera from "./Camera";
import ButtonIcon, { ToolbarSeparator } from "./ButtonIcon";
import ChatPanel from "./ChatPanel";

import axiosConfig from "../../utils/axiosConfig";
import { getPeerConfig } from "../../utils/configPeer";
import {
  constraints,
  startCamera,
  stopCamera,
  updateAudio,
  updateVideo,
} from "../../utils/camera";
import { setVideoBitrate, preferVP9Codec } from "../../utils/webrtcQuality";
import {
  createSessionRecorder,
  isRecordingSupported,
} from "../../utils/recorder";

import soundWAW from "../../sound/call.wav";

import AuthContext from "../../context/AuthContext";

export default class WebRTC extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isPeer: false,
      isCamera: false,
      isCall: false,
      isMarker: false,
      isMuted: false,
      isCameraDisabled: false,
      token: null,
      localVideoWidth: null,
      localVideoHeight: null,
      remoteVideoWidth: null,
      remoteVideoHeight: null,
      isTakePicture: false,
      pictures: [],
      statusConnection: 0,
      zoomLevel: 1,
      chatMessages: [],
      isChatOpen: false,
      unreadChatCount: 0,
      lastReceivedMessage: null,
      recordState: "idle",
    };

    // WebRTC constraints
    this.constraints = constraints;
    // Components ref
    this.cameraRef = createRef();
    this.localVideoRef = createRef();
    this.remoteVideoRef = createRef();
    // Call sound
    this.audio = new Audio(soundWAW);
  }

  static contextType = AuthContext;

  setDefaultState = (isPeer, callback = () => {}) => {
    this.setState(
      {
        isPeer: isPeer,
        isCamera: false,
        isCall: false,
        isMarker: false,
        isMuted: false,
        isCameraDisabled: false,
        token: null,
        localVideoWidth: null,
        localVideoHeight: null,
        remoteVideoWidth: null,
        remoteVideoHeight: null,
        isTakePicture: false,
        statusConnection: 0,
        zoomLevel: 1,
        chatMessages: [],
        isChatOpen: false,
        unreadChatCount: 0,
        lastReceivedMessage: null,
        recordState: "idle",
      },
      callback
    );
  };

  componentDidMount() {
    if (!document.location.href.includes("client")) {
      this.initPeer();
    }
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    this.stopRecording();
  }

  handleBeforeUnload = (event) => {
    if (this.state.recordState !== "recording") return;
    event.preventDefault();
    event.returnValue = "";
  };

  initPeer = async () => {
    const config = await getPeerConfig();
    this.peer = new Peer(`xpertvisio_${this.context.id}_expert`, config);

    this.peer.on("open", () => {
      this.setState({ isPeer: true });
    });

    this.peer.on("call", (incomingCall) => {
      console.log("incomingCall");
    });

    this.peer.on("connection", (conn) => {
      this.conn = conn;
      this.conn.on("data", (data) => {
        switch (data.type) {
          case "call":
            if (!this.state.isCall) {
              this.setState({ token: data.token, isCall: true }, () => {
                this.audio.currentTime = 0;
                if (!this.state.isCamera && !this.state.isForce) {
                  this.audio.play();
                }
              });
            } else if (this.state.token === data.token && this.state.isCamera) {
              // Le client revient et l'expert est déjà dans la room
              console.log("Client reconnecté - expert déjà en visio");
              this.handleStartCallClick();
            }
            break;
          case "call-reconnect":
            if (this.state.token === data.token) {
              this.handleAcceptClick(true);
            }
            break;
          case "close-call":
            this.handleStopCameraClick();
            break;
          case "return-picture":
            this.getPicture(data.id);
            break;
          case "picture-error":
            alert(
              "La photo n'a pas été prise : la caméra de l'assuré ne renvoie plus d'image. Demandez-lui de raccrocher puis de relancer la visio."
            );
            break;
          case "record-consent":
            this.handleRecordConsent(data.accepted);
            break;
          case "record-revoke":
            this.stopRecording();
            break;
          case "chat-message":
            this.setState((prev) => ({
              chatMessages: [...prev.chatMessages, data],
              unreadChatCount: prev.isChatOpen
                ? 0
                : prev.unreadChatCount + 1,
              lastReceivedMessage: prev.isChatOpen ? null : data,
            }));
            break;
          default:
            break;
        }
      });
    });

    this.peer.on("disconnect", () => {
      console.log("disconnect");
      this.setState({ isPeer: false });
      // this.setDefaultState(false);
    });
    this.peer.on("error", (err) => {
      console.log("error", err);
      this.setState({ isPeer: false }, () => {
        setTimeout(() => {
          this.initPeer();
        }, 10000);
      });
      // this.setDefaultState(false);
    });
  };

  // start getPicture

  getPicture = (id) => {
    axiosConfig("folder/get-picture", {
      data: {
        id,
      },
    })
      .then((res) => {
        if (!res.data.success) {
          this.getPictureError();
        } else {
          let tmpPictures = this.state.pictures;
          tmpPictures.push({
            id,
            base64: res.data.picture.base64,
          });
          this.setState({ pictures: tmpPictures });
        }
      })
      .catch(this.getPictureError);
  };

  getPictureError = () => {
    alert("Une erreur est survenue lors de la suppression de l'image");
  };

  // end getPicture

  // start deletePicture

  handleDeletePictureClick = (id) => {
    this.setState((prev) => ({
      pictures: prev.pictures.filter((picture) => picture.id !== id),
    }), () => {
      axiosConfig("folder/delete-picture", {
        data: {
          id,
        },
      })
        .then((res) => {
          if (!res.data.success) {
            this.deletePictureError();
          }
        })
        .catch(this.deletePictureError);
    });
  };

  deletePictureError = () => {
    alert("Une erreur est survenue lors de la suppression de l'image");
  };

  // end deletePicture

  handleStartCallClick = () => {
    this.call = this.peer.call(
      `${this.state.token}_client`,
      this.localVideoRef.current.srcObject
    );

    if (!this.call) {
      console.error(
        "Échec de l'initialisation de l'appel - le client n'est peut-être pas connecté"
      );
      this.handleStopCameraClick(false);
      return;
    }

    preferVP9Codec(this.call.peerConnection);

    this.call.on("stream", (remoteStream) => {
      this.remoteVideoRef.current.srcObject = remoteStream;
      this.remoteVideoRef.current.addEventListener(
        "playing",
        this.playingRemoteEventListener,
        false
      );
    });

    this.call.peerConnection.onconnectionstatechange = () => {
      const state = this.call.peerConnection.connectionState;
      console.log("State:", state);

      if (state === "connected") {
        this.setState({ statusConnection: 2 });

        setVideoBitrate(this.call.peerConnection, 2_500_000);

        // Démarre la vérif qualité toutes les 2 secondes
        this.statsInterval = setInterval(this.checkConnectionQuality, 2000);
      } else if (["disconnected", "failed", "closed"].includes(state)) {
        clearInterval(this.statsInterval);
        this.setState({ statusConnection: 0 });
      }
    };
  };

  playingLocalEventListener = () => {
    this.setState(
      {
        localVideoWidth: this.localVideoRef.current.videoWidth,
        localVideoHeight: this.localVideoRef.current.videoHeight,
      },
      () => {
        this.handleStartCallClick();
      }
    );
  };

  playingRemoteEventListener = () => {
    this.setState({
      remoteVideoWidth: this.remoteVideoRef.current.videoWidth,
      remoteVideoHeight: this.remoteVideoRef.current.videoHeight,
    });
  };

  handleAcceptClick = (isForce = false) => {
    this.audio.pause();
    startCamera(this.constraints, (result) => {
      if (result.success) {
        this.setState({ isCamera: true, isForce: isForce }, () => {
          window.stream = result.stream;
          this.localVideoRef.current.srcObject = result.stream;
          this.localVideoRef.current.addEventListener(
            "playing",
            this.playingLocalEventListener,
            false
          );
        });
      }
    });
  };

  handleRejectClick = () => {
    this.audio.pause();
    this.conn.send({ type: "reject-call" });
    this.setState({
      token: null,
      isCall: false,
      isCamera: false,
    });
  };

  handleStopCameraClick = (isSend = false) => {
    // Flush the recorder first: stopCamera kills the tracks it is reading.
    this.stopRecording(() => {
      this.stopCall(isSend);
    });
  };

  stopCall = (isSend) => {
    stopCamera(() => {
      this.setDefaultState(true, () => {
        if (isSend) {
          if (this.conn) this.conn.send({ type: "close-call" });
        } else {
          if (this.call) this.call.close();
          if (this.conn) this.conn.close();
        }
      });
    });
  };

  handleTakePictureClick = () => {
    if (!this.conn) return;
    this.setState({ isTakePicture: false }, () =>
      this.setState({ isTakePicture: true }, () => {
        this.conn.send({ type: "take-picture" });
      })
    );
  };

  handleRecordClick = () => {
    if (this.state.recordState === "recording") {
      this.stopRecording();
      return;
    }

    if (this.state.recordState !== "idle" || !this.conn) return;

    this.setState({ recordState: "pending" }, () => {
      this.conn.send({ type: "record-request" });
      // No answer is a refusal: the recording never starts on silence.
      this.consentTimeout = setTimeout(() => {
        if (this.state.recordState === "pending") {
          this.setState({ recordState: "idle" });
          alert("L'assuré n'a pas répondu à la demande d'enregistrement");
        }
      }, 30000);
    });
  };

  handleRecordConsent = (accepted) => {
    clearTimeout(this.consentTimeout);

    if (this.state.recordState !== "pending") return;

    if (!accepted) {
      this.setState({ recordState: "idle" });
      alert("L'assuré a refusé l'enregistrement");
      return;
    }

    this.recorder = createSessionRecorder({
      remoteVideo: this.remoteVideoRef.current,
      localVideo: this.localVideoRef.current,
      markerCanvas: this.cameraRef.current
        ? this.cameraRef.current.getCanvas()
        : null,
      folderToken: this.state.token,
      onError: this.handleRecordError,
      onAutoStop: () => this.stopRecording(),
    });

    this.recorder.start((result) => {
      if (!result.success) {
        this.recorder = null;
        this.setState({ recordState: "idle" });
        alert("Impossible de démarrer l'enregistrement");
        return;
      }

      this.setState({ recordState: "recording" }, () => {
        if (this.conn) this.conn.send({ type: "record-started" });
      });
    });
  };

  handleRecordError = () => {
    this.stopRecording();
    alert("L'enregistrement a été interrompu");
  };

  stopRecording = (callback = () => {}) => {
    clearTimeout(this.consentTimeout);

    if (!this.recorder) {
      if (this.state.recordState !== "idle") {
        this.setState({ recordState: "idle" });
      }
      return callback();
    }

    const recorder = this.recorder;
    this.recorder = null;

    this.setState({ recordState: "idle" }, () => {
      if (this.conn) this.conn.send({ type: "record-stopped" });

      // Hanging up must not wait forever on a stalled upload.
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        callback();
      };

      setTimeout(finish, 10000);
      recorder.stop(finish);
    });
  };

  handleZoomIn = () => {
    this.setState(
      (prev) => ({
        zoomLevel: Math.min(prev.zoomLevel + 0.5, 5),
      }),
      () => {
        this.conn.send({ type: "zoom", level: this.state.zoomLevel });
      }
    );
  };

  handleZoomOut = () => {
    this.setState(
      (prev) => ({
        zoomLevel: Math.max(prev.zoomLevel - 0.5, 1),
      }),
      () => {
        this.conn.send({ type: "zoom", level: this.state.zoomLevel });
      }
    );
  };

  handleSendChatMessage = (text) => {
    if (!text.trim()) return;
    const message = {
      type: "chat-message",
      text: text.trim(),
      sender: "expert",
      timestamp: Date.now(),
    };
    this.conn.send(message);
    this.setState((prev) => ({
      chatMessages: [...prev.chatMessages, message],
    }));
  };

  handleToggleChat = () => {
    this.setState((prev) => ({
      isChatOpen: !prev.isChatOpen,
      unreadChatCount: !prev.isChatOpen ? 0 : prev.unreadChatCount,
      lastReceivedMessage: !prev.isChatOpen ? null : prev.lastReceivedMessage,
    }));
  };

  handlePointMarkerClick = (x, y, width, height) => {
    this.conn.send({ type: "point-marker", x, y, width, height });
  };

  handleClearMarkerClick = () => {
    this.cameraRef.current.clearCanvas();
    this.conn.send({ type: "clear-marker" });
  };

  handleUpdateAudioClick = () => {
    updateAudio(this.state.isMuted);
    this.setState({
      isMuted: !this.state.isMuted,
    });
  };

  handleUpdateVideoClick = () => {
    updateVideo(this.state.isCameraDisabled);
    this.setState({
      isCameraDisabled: !this.state.isCameraDisabled,
    });
  };

  handleReverseCameraClick = () => {
    this.conn.send({ type: "reverse-camera" });
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.isCall &&
      this.state.isCall !== prevState.isCall &&
      this.state.isForce
    ) {
      this.handleAcceptClick(true);
    }
  }

  checkConnectionQuality = async () => {
    if (!this.call || !this.call.peerConnection) return;

    const stats = await this.call.peerConnection.getStats();
    let quality = {
      bitrateKbps: null,
      packetLoss: null,
      rtt: null,
      jitter: null,
    };

    stats.forEach((report) => {
      // RTT
      if (report.type === "candidate-pair" && report.state === "succeeded") {
        quality.rtt = (report.currentRoundTripTime * 1000).toFixed(0); // ms
      }

      // Audio
      if (report.type === "inbound-rtp" && report.kind === "audio") {
        quality.jitter = (report.jitter * 1000).toFixed(0); // ms
        quality.packetLoss = report.packetsLost;
      }

      // Bitrate vidéo
      if (report.type === "outbound-rtp" && report.kind === "video") {
        if (this.prevBytesSent && this.prevTimestamp) {
          const deltaBytes = report.bytesSent - this.prevBytesSent;
          const deltaTime = report.timestamp - this.prevTimestamp;
          const bitrate = (deltaBytes * 8) / (deltaTime / 1000);
          quality.bitrateKbps = (bitrate / 1000).toFixed(1);
        }
        this.prevBytesSent = report.bytesSent;
        this.prevTimestamp = report.timestamp;
      }
    });

    // Enregistre la qualité dans le state
    this.setState({ connectionQuality: quality });
  };

  render() {
    if (!document.location.href.includes("client")) {
      return (
        <div style={{ position: "absolute", zIndex: 100, height: 0, width: 0 }}>
          <StatusWebRTC isPeer={this.state.isPeer} />
          {this.state.token ? (
            <CallAlertWebRTC
              handleRejectClick={this.handleRejectClick}
              handleAcceptClick={() => this.handleAcceptClick()}
              token={this.state.token}
              isCall={this.state.isCall}
            />
          ) : null}
          {this.state.isCamera ? (
            <>
              <Camera
                connectionQuality={this.state.connectionQuality}
                ref={this.cameraRef}
                secondVideoWidth={this.state.localVideoWidth}
                secondVideoHeight={this.state.localVideoHeight}
                secondVideoRef={this.localVideoRef}
                secondVideoIsMuted={true}
                firstVideoWidth={this.state.remoteVideoWidth}
                firstVideoHeight={this.state.remoteVideoHeight}
                firstVideoRef={this.remoteVideoRef}
                isMarker={this.state.isMarker}
                onMarker={this.handlePointMarkerClick}
                isTakePicture={this.state.isTakePicture}
                pictures={this.state.pictures}
                onDeletePicture={this.handleDeletePictureClick}
                statusConnection={this.state.statusConnection}
                zoomLevel={this.state.zoomLevel}
                isRecording={this.state.recordState === "recording"}
              >
                <ButtonIcon
                  onClick={() => this.handleTakePictureClick()}
                  icon={faCamera}
                  title="Prendre une photo"
                />
                {isRecordingSupported() ? (
                  <ButtonIcon
                    onClick={this.handleRecordClick}
                    red={this.state.recordState !== "idle"}
                    icon={
                      this.state.recordState === "recording"
                        ? faStopCircle
                        : faCircle
                    }
                    title={
                      this.state.recordState === "recording"
                        ? "Arrêter l'enregistrement"
                        : "Enregistrer"
                    }
                  />
                ) : null}
                <ToolbarSeparator />
                <ButtonIcon
                  onClick={this.handleZoomIn}
                  icon={faSearchPlus}
                  title="Zoom +"
                />
                <ButtonIcon
                  onClick={this.handleZoomOut}
                  icon={faSearchMinus}
                  title="Zoom −"
                />
                <ButtonIcon
                  onClick={() => {
                    this.setState({ isMarker: !this.state.isMarker });
                  }}
                  green={this.state.isMarker}
                  icon={faPen}
                  title="Annoter"
                />
                <ButtonIcon
                  onClick={this.handleClearMarkerClick}
                  icon={faTrash}
                  title="Effacer les annotations"
                />
                <ToolbarSeparator />
                <ButtonIcon
                  onClick={this.handleUpdateAudioClick}
                  red={this.state.isMuted}
                  icon={
                    this.state.isMuted
                      ? faMicrophoneAltSlash
                      : faMicrophoneAlt
                  }
                  title={
                    this.state.isMuted ? "Réactiver le micro" : "Couper le micro"
                  }
                />
                <ButtonIcon
                  onClick={this.handleUpdateVideoClick}
                  red={this.state.isCameraDisabled}
                  icon={
                    this.state.isCameraDisabled ? faVideoSlash : faVideo
                  }
                  title={
                    this.state.isCameraDisabled
                      ? "Réactiver la caméra"
                      : "Couper la caméra"
                  }
                />
                <ButtonIcon
                  onClick={this.handleReverseCameraClick}
                  icon={faSyncAlt}
                  title="Changer de caméra"
                />
                <ButtonIcon
                  onClick={this.handleToggleChat}
                  green={this.state.isChatOpen}
                  icon={faComment}
                  title="Messages"
                  badge={
                    this.state.unreadChatCount > 0
                      ? this.state.unreadChatCount
                      : null
                  }
                />
                <ButtonIcon
                  onClick={() => this.handleStopCameraClick(true)}
                  icon={faPhoneSlash}
                  danger
                  separated
                  title="Raccrocher"
                />
              </Camera>
              <ChatPanel
                messages={this.state.chatMessages}
                onSend={this.handleSendChatMessage}
                isOpen={this.state.isChatOpen}
                onClose={this.handleToggleChat}
                currentSender="expert"
                lastReceivedMessage={this.state.lastReceivedMessage}
              />
            </>
          ) : null}
        </div>
      );
    } else {
      return <></>;
    }
  }
}
